import React, { ChangeEvent, FC, useRef } from 'react';
import classNames from 'classnames';
import { Loader } from 'rsuite';
import {
	alephToast,
	Avatar, AvatarSize, Button, ButtonAppearance, ButtonColor, EditIcon, NotificationToast, NotificationType,
} from '@aleph/shared-components';
import { useTranslation } from 'react-i18next';

export type ProfileImage = {
	file: File,
	src: string,
};

interface ProfileImageUploaderProps {
	type?: 'User' | 'Team' | 'Advertiser' | undefined,
	onUploadProfileImage: (image: ProfileImage | undefined) => void,
	profileImage?: string | { src?: string } | null
	readOnly?: boolean,
	loading?: boolean,
	disabled?: boolean,
}

const ProfileImageUploader: FC<ProfileImageUploaderProps> = ({
	onUploadProfileImage, profileImage, readOnly, loading, disabled,
}) => {
	const { t } = useTranslation();
	const uploadRef = useRef<HTMLInputElement>(null);

	const onChangeImage = (e: ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files?.[0]) {
			onUploadProfileImage(undefined);

			return;
		}

		if (!['image/jpeg', 'image/png', 'image/gif'].includes(e.target.files?.[0]?.type)) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('re: wrong file format', 'Wrong format file, supported formats are: jpeg, png, gif')}
				/>,
			);

			onUploadProfileImage(undefined);

			return;
		}

		onUploadProfileImage({
			file: e.target.files?.[0],
			src:  URL.createObjectURL(e.target.files?.[0]),
		});
	};

	if (loading) {
		return (
			<div className={
				classNames(
					'w-[170px] h-[170px] rounded-full bg-blue-200 text-blue',
					'flex items-center justify-center relative',
					{
						'!bg-white': profileImage,
					},
				)}
			>
				<Loader size='lg'
					color='text-gray-800'
				/>
			</div>
		);
	}

	return (
		<div className={
			classNames(
				'w-[167px] h-[167px] rounded-full bg-blue-200 text-blue',
				'flex items-center justify-center relative',
				{
					'!bg-white': profileImage,
				},
			)}
		>
			{
				profileImage && (
					<Avatar
						avatarSource={
							typeof profileImage !== 'string'
								? profileImage?.src || ''
								: profileImage
						}
						alt='profile'
						size={AvatarSize.ExtraLarge}
					/>
				)
			}

			{(!readOnly && !disabled)
				&& <div className="absolute bottom-0 right-0 bg-gray-0 p-[4px] rounded-[6px]">
					<Button
						leftNode={<EditIcon />}
						appearance={ButtonAppearance.OUTLINED}
						color={ButtonColor.GRAY}
						onClick={() => uploadRef.current?.click()}
					/>
				</div>
			}

			<input
				ref={uploadRef}
				onChange={onChangeImage}
				type="file"
				className="display-none"
				accept="image/png, image/jpeg, image/gif"
			/>
		</div>
	);
};

export default ProfileImageUploader;
